import { webApi } from '@mi-front/api-clients';
import { Box, Grid } from '@mui/material';
import { StatusNoActivityMessage } from '../../../status/components/status/activity/StatusNoActivityMessage';
import { useStatusSettingsStore } from '../../../status/stores/status-settings.store';
import { useYoutubeRecentBroadcastsQuery } from '../broadcasts/recent/useYoutubeRecentBroadcasts';
import { YoutubeBroadcastItem } from '../broadcasts/item/YoutubeBroadcastItem';
import { YoutubeBroadcastItemSkeleton } from '../broadcasts/item/YoutubeBroadcastItemSkeleton';
import { useYoutubePanoramaVisibleCount } from './useYoutubePanoramaVisibleCount';
import { getYoutubePanoramaBroadcastItemSize } from './youtube-panorama-broadcast-item-size';
import { YoutubePanoramaCardsHeader } from './YoutubePanoramaCardsHeader';
import { Station } from '@app/core/types/stations';

interface Props {
  station: Station;
  compact?: boolean;
  height: number;
}

const HEADER_BLOCK_HEIGHT = {
  compact: 24,
  regular: 28,
};

const wrapperSx = (compact: boolean) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  px: compact ? 1.5 : 2,
});

/** List view caps card width; grid view (compact) keeps a single card at full width. */
const panoramaGridItemSize = (compact: boolean) => (compact ? { xs: 12 } : { xs: 6, lg: 6, xl: 4 });

/** Horizontal strip of recent YouTube broadcasts for offline YouTube Native stations. */
export function YoutubePanoramaCards({ station, compact = false, height }: Props) {
  const view = useStatusSettingsStore((state) => state.view);
  const { data, isLoading } = useYoutubeRecentBroadcastsQuery(station.id);
  const broadcasts = data?.data ?? [];
  const visibleCount = useYoutubePanoramaVisibleCount(compact);
  const visibleBroadcasts = broadcasts.slice(0, visibleCount);
  const itemSize = getYoutubePanoramaBroadcastItemSize(view);
  const headerBlockHeight = compact ? HEADER_BLOCK_HEIGHT.compact : HEADER_BLOCK_HEIGHT.regular;
  const cardHeight = height - headerBlockHeight - (compact ? 8 : 12);
  const gridItemSize = panoramaGridItemSize(compact);

  if (!isLoading && broadcasts.length === 0) {
    return (
      <Box sx={{ ...wrapperSx(compact), height }}>
        <StatusNoActivityMessage fillContainer />
      </Box>
    );
  }

  return (
    <Box sx={{ ...wrapperSx(compact), height }}>
      <YoutubePanoramaCardsHeader stationSlug={station.slug} compact={compact} />
      <Grid container spacing={1} sx={{ overflow: 'hidden' }}>
        {isLoading
          ? Array.from({ length: visibleCount }, (_, i) => (
              <Grid item {...gridItemSize} key={i}>
                <YoutubeBroadcastItemSkeleton
                  layout="horizontal"
                  size={itemSize}
                  height={cardHeight}
                />
              </Grid>
            ))
          : visibleBroadcasts.map((broadcast: webApi.BroadcastDto) => (
              <Grid
                item
                {...gridItemSize}
                key={`${broadcast.streamStartedAt}-${broadcast.streamEndedAt}`}
              >
                <YoutubeBroadcastItem
                  broadcast={broadcast}
                  stationSlug={station.slug}
                  layout="horizontal"
                  size={itemSize}
                  height={cardHeight}
                />
              </Grid>
            ))}
      </Grid>
    </Box>
  );
}
